import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useSession } from '@/lib/auth';
import { useActiveLocationId } from '@/lib/locationContext';
import { viewAsProId, viewRpcArgs } from '@/lib/viewAs';
import { isCell } from '@/lib/cell/config';
import { cellGet, viewQuery } from '@/lib/cell/api';

/**
 * IMAGE PRO ACADEMY SESSIONS (ACADEMY-1; Peter, 2026-10-28, items 5190a483 / 91db683e; backend
 * Salonsuites/image-studios-backend#1135). Corporate publishes its live classes only as checkbox questions
 * on a public Google Form; the backend reads that form daily into hub.academy_session and the studio texts
 * every pro 7 and 2 days before a class with a link here. This file is the Pro Hub's half: the upcoming
 * classes at the pro's studio (public.academy_upcoming — empty while the studio's switch
 * location_configs.metadata.academy_reminders_enabled is off), the pro's "Going" tap (academy_set_going),
 * and the Register link, which opens corporate's own form PREFILLED with the pro's name, email and studio
 * and the class ticked. Registration and its confirmation emails stay corporate's.
 */

export interface AcademySession {
  id: string;
  title: string;
  /** Corporate's own words for the date, verbatim ("Monday, November 9, at 12 pm Pacific, …"). */
  date_line: string | null;
  starts_at: string;
  /** The form's four time zones disagreed; starts_at is the majority reading. The card says "check the time". */
  time_conflict: boolean;
  /** The class checkbox's Google Forms entry id — what the Register link ticks. */
  entry_id: string;
  going: boolean;
}

/** The form corporate registers pros on (read 2026-10-28; the backend's academy-sync names the same id). */
export const ACADEMY_FORM_ID = '1FAIpQLSdB_t-EvcXF127hlIS7aWKENgoaYfSAP0r_PL-5X1unulDGbw';
export const ACADEMY_FORM_URL = `https://docs.google.com/forms/d/e/${ACADEMY_FORM_ID}/viewform`;

/** The form's text fields, by entry id (read from the form 2026-10-28; a changed form breaks only the prefill). */
export const FORM_FIELDS = {
  name: '135923745',
  email: '1857365536',
  email_again: '1804783226',
  location: '172209218',
} as const;

/** Corporate's form, prefilled with who the pro is and which class, so Register is one tap. */
export function registerUrl(
  session: Pick<AcademySession, 'entry_id' | 'date_line'>,
  pro: { name: string; email: string; studio: string },
): string {
  const p = new URLSearchParams();
  p.set('usp', 'pp_url');
  if (pro.name) p.set(`entry.${FORM_FIELDS.name}`, pro.name);
  if (pro.email) {
    p.set(`entry.${FORM_FIELDS.email}`, pro.email);
    p.set(`entry.${FORM_FIELDS.email_again}`, pro.email);
  }
  if (pro.studio) p.set(`entry.${FORM_FIELDS.location}`, pro.studio);
  // A checkbox is ticked by naming its option text — the date line is the option.
  if (session.date_line) p.set(`entry.${session.entry_id}`, session.date_line);
  return `${ACADEMY_FORM_URL}?${p.toString()}`;
}

/** "Mon, Nov 9 · 3:00 PM EST" on the viewer's clock. */
export function whenLabel(iso: string, tz?: string): string {
  const d = new Date(iso);
  const day = new Intl.DateTimeFormat('en-US', { timeZone: tz, weekday: 'short', month: 'short', day: 'numeric' }).format(d);
  const time = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit', timeZoneName: 'short' }).format(d);
  return `${day} · ${time.replace(/ /g, ' ')}`;
}

/** Days from now until the class, on calendar days (0 = today). */
export function daysUntil(iso: string, now: Date = new Date()): number {
  const a = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const d = new Date(iso);
  const b = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((b - a) / 86_400_000);
}

/** Upcoming classes at the active studio — [] while the studio's switch is off. */
export function useAcademySessions() {
  const { session } = useSession();
  const userId = session?.user?.id;
  const { activeLocationId } = useActiveLocationId();
  const viewing = viewAsProId();
  return useQuery<AcademySession[]>({
    queryKey: ['pro-academy', userId, activeLocationId, viewing],
    enabled: !!userId && !!activeLocationId,
    staleTime: 60_000,
    queryFn: async () => {
      if (isCell()) {
        // CELL (M28): GET /v1/pro-hub/locations/{locationId}/academy — the same rpc (academy_upcoming).
        const { data, error } = await cellGet<{ rows: AcademySession[] }>(`/v1/pro-hub/locations/${encodeURIComponent(activeLocationId!)}/academy`, viewQuery());
        if (error) throw new Error(error.message);
        return (data?.rows ?? []) as AcademySession[];
      }
      const { data, error } = await supabase.rpc('academy_upcoming', { p_location: activeLocationId!, ...viewRpcArgs() });
      if (error) throw new Error(error.message);
      return (data ?? []) as AcademySession[];
    },
  });
}

/** The pro's Going tap. Refused under view-as (the client's read-only fence) and server-side (no pro row). */
export function useSetGoing() {
  const qc = useQueryClient();
  const { activeLocationId } = useActiveLocationId();
  return useMutation({
    mutationFn: async ({ sessionId, going }: { sessionId: string; going: boolean }) => {
      if (isCell()) {
        // CELL (M28): POST /v1/pro-hub/academy/{sessionId}/going — no endpoint yet; the tap is refused, not lost quietly.
        throw new Error('Going is not available on this build yet.');
      }
      const { data, error } = await supabase.rpc('academy_set_going', {
        p_session: sessionId, p_location: activeLocationId!, p_going: going,
      });
      if (error) throw new Error(error.message);
      if (data !== true) throw new Error('Could not save that.');
      return true;
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['pro-academy'] }); },
  });
}
